import type {
	DocumentCatalogItem,
	PartInventoryItem,
} from "../models/Catalogs";
import { useCatalogReload, useCatalogUpdatedAt } from "../hooks/useCatalogs";
import { CatalogFreshness } from "./CatalogFreshness";

/**
 * Ready to drop at the bottom of any picker: shows when the list was last brought in and reloads
 * it past both copies, the browser's and the API's. Wired up here rather than in each picker so
 * every one of them behaves the same and none has to know about the catalog hooks.
 * `onReloaded` receives the fresh list, because each picker keeps it in its own shape.
 */
type Props<T> = {
	onReloaded?: (rows: T[]) => void;
	onError?: (message: string) => void;
};

export const PartsCatalogFreshness = ({
	onReloaded,
	onError,
}: Props<PartInventoryItem>) => {
	const { partsUpdatedAt } = useCatalogUpdatedAt();
	const { reloadPartsInventory } = useCatalogReload();

	return (
		<CatalogFreshness
			updatedAt={partsUpdatedAt}
			onError={onError}
			onReload={async () => {
				const parts = await reloadPartsInventory();
				onReloaded?.(parts);
				return parts;
			}}
		/>
	);
};

export const DocumentsCatalogFreshness = ({
	onReloaded,
	onError,
}: Props<DocumentCatalogItem>) => {
	const { documentsUpdatedAt } = useCatalogUpdatedAt();
	const { reloadDocumentsCatalog } = useCatalogReload();

	return (
		<CatalogFreshness
			updatedAt={documentsUpdatedAt}
			onError={onError}
			onReload={async () => {
				const documents = await reloadDocumentsCatalog();
				onReloaded?.(documents);
				return documents;
			}}
		/>
	);
};
